<script lang="ts">
import { usePrizesStore } from '@/stores/prizes'
import type { Nullable, Prize, Winner } from '@/types'
import { capitalCase } from 'change-case'
import { type PropType, defineComponent } from 'vue'

import BaseCard from './new/BaseCard.vue'

/**
 * A card to display a winner.
 *
 */
export default defineComponent({
  components: {
    BaseCard,
  },
  props: {
    /**
     * A configured winner's object.
     */
    winner: {
      type: Object as PropType<Winner>,
      required: true,
    },
  },
  setup() {
    return {
      capitalCase,
      prizeStore: usePrizesStore(),
    }
  },
  data() {
    return {
      prize: null as Nullable<Prize>,
    }
  },
  async mounted() {
    this.prize = await this.prizeStore.fetchPrize(this.winner.prize_code)
  },
  methods: {
    open(openVideoModal?: () => void) {
      if (!this.prize) {
        console.error('Prize not found')
        return
      }

      if (this.winner.youtube_id && openVideoModal) {
        return openVideoModal()
      }

      return this.$router.push({
        name: 'live-draw',
        params: { prizeCode: this.prize.code },
      })
    },
  },
})
</script>

<template>
  <BaseCard class="gap-0 p-0">
    <AppYouTubeModal
      v-if="prize"
      :id="winner.youtube_id ?? winner.name"
      :title="`Watch a video of ${winner.name} winning`"
    >
      <template #default="{ openModal }">
        <div class="flex size-full flex-grow flex-col" @click="open(openModal)">
          <div
            class="mask-squiggle"
            :style="{
              '--mask-squiggle-url': `url('/assets/images/masks/memberships-background.svg')`,
            }"
          >
            <img
              :src="winner.image"
              :alt="winner.name"
              class="aspect-square size-full object-cover"
            />

            <button
              v-if="winner.youtube_id"
              class="absolute bottom-3 right-2 w-max rounded-100 bg-black/70 px-2 py-1 text-white"
              :aria-label="prize ? `Watch ${prize.title} draw video now` : ''"
            >
              <AppIcon
                :icon="Icons.Play"
                class="rounded-full bg-tertiary p-[2px]"
              />
              Watch now
            </button>
          </div>

          <div
            v-if="prize"
            class="flex items-center justify-center gap-2 bg-primary px-3 py-2 text-white flex-wrap"
          >
            <span class="text-lg font-normal leading-[95%] whitespace-nowrap"
              >Prize Drawn:</span
            >
            <span class="text-lg font-bold leading-[95%] whitespace-nowrap">{{
              $d(prize.drawn_at.toJSDate(), {
                year: 'numeric',
                month: 'numeric',
                day: 'numeric',
              })
            }}</span>
          </div>

          <div class="flex flex-grow flex-col p-3 text-center text-black">
            <p class="text-center text-xl font-extrabold">
              {{ winner.name }}
            </p>

            <div class="flex flex-grow flex-col gap-3">
              <p v-if="prize">
                Won this incredible<br />
                <span class="font-semibold">{{ prize.title }}</span>
              </p>
            </div>
            <hr class="my-3" />
            <div class="flex-1 text-sm">
              Ticket number:
              <p class="truncate">{{ winner.ticket }}</p>
            </div>
          </div>
        </div>
      </template>
    </AppYouTubeModal>
  </BaseCard>
</template>
